import { oc } from "@orpc/contract";
import { z } from "zod";
import { FileStreamSchema } from "../../schemas/common";

/**
 * Transaction schema — uses passthrough because the column set is dynamic
 * (defined by pipeline workflow config, not the contract). Only `id` is guaranteed.
 */
const TransactionSchema = z
  .object({
    id: z.string(),
  })
  .passthrough();

const PaginationSchema = z.object({
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});

/** Matches snake_case column names (lowercase start, then lowercase/digits/_). */
const ColumnNameSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/, "Invalid column name");

// Transaction lifecycle status. "posted" is NOT a status — it's an orthogonal
// fact tracked by the `posted_at` timestamp (a posted row stays `staged`). Use
// PostingStatusFilterSchema for GL-posting filters that distinguish staged vs
// posted. "deleted" is retained for legacy rows (normalized to no_claim on read).
const SdE844dStatusSchema = z.enum([
  "pending",
  "staged",
  "staged_internal",
  "processing",
  "sent",
  "resubmission_pending",
  "no_claim",
  "deleted",
]);

/**
 * Logical status filter for GL-posting flows (download + post). "posted" here
 * means the derived flag (staged AND posted_at set); "staged" means un-posted
 * staged. The backend translates these via buildSdStatusWhere.
 */
const PostingStatusFilterSchema = z.enum(["staged", "posted", "sent"]);

// New consolidated List input — superset of all existing list schemas
export const ListSdTransactionsInputSchema = z.object({
  statuses: z.array(SdE844dStatusSchema).min(1),
  limit: z.number().int().positive().max(1000).optional().default(100),
  offset: z.number().int().nonnegative().optional().default(0),
  columnFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  textFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  dateFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  numericFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  hasComments: z.boolean().optional(),
  /** Only claims whose DUNS no SPA active on the invoice date names (Schneider). */
  dunsNotOnSpa: z.boolean().optional(),
  search: z.string().optional(),
  searchColumns: z.array(ColumnNameSchema).optional(),
  sortBy: z.string().optional(),
  sortDirection: z.enum(["asc", "desc"]).optional().default("desc"),
  transactionIds: z.array(z.string().uuid()).optional(),
});

export const ListSdTransactionsResponseSchema = z.object({
  transactions: z.array(TransactionSchema),
  pagination: PaginationSchema,
});

export const ListSdTransactionIdsInputSchema = z.object({
  statuses: z.array(SdE844dStatusSchema).min(1),
  columnFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  textFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  dateFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  numericFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  hasComments: z.boolean().optional(),
  /** Only claims whose DUNS no SPA active on the invoice date names (Schneider). */
  dunsNotOnSpa: z.boolean().optional(),
  search: z.string().optional(),
  searchColumns: z.array(ColumnNameSchema).optional(),
});

export const ListSdTransactionIdsResponseSchema = z.object({
  transactionIds: z.array(z.string()),
});

/**
 * Mark Transactions As No-Claim
 */
export const MarkAsNoClaimInputSchema = z.object({
  transactionIds: z.array(z.string()).min(1),
});

export const MarkAsNoClaimResponseSchema = z.object({
  markedCount: z.number().int().nonnegative(),
});

/**
 * Unmark Transactions From No-Claim
 */
export const UnmarkAsNoClaimInputSchema = z.object({
  transactionIds: z.array(z.string()).min(1),
});

export const UnmarkAsNoClaimResponseSchema = z.object({
  unmarkedCount: z.number().int().nonnegative(),
});

/**
 * Stage Transactions
 */
export const StageTransactionsInputSchema = z.object({
  transactionIds: z.array(z.string()).min(1),
});

export const StageTransactionsResponseSchema = z.object({
  stagedCount: z.number().int().nonnegative(),
});

/**
 * Unstage Transactions
 */
export const UnstageTransactionsInputSchema = z.union([
  z.object({ transactionIds: z.array(z.string()).min(1) }),
  z.object({ all: z.literal(true) }),
]);

export const UnstageTransactionsResponseSchema = z.object({
  unstagedCount: z.number().int().nonnegative(),
});

/**
 * Un-post Transactions — reverse a GL posting so the rows can be edited again.
 * Clears posted_at (back to plain `staged`); does not unstage. Admin/posting
 * action.
 */
export const UnpostTransactionsInputSchema = z.object({
  transactionIds: z.array(z.string()).min(1),
});

export const UnpostTransactionsResponseSchema = z.object({
  unpostedCount: z.number().int().nonnegative(),
});

/**
 * Update Staged Transaction
 */
export const UpdateStagedTransactionInputSchema = z.object({
  transactionId: z.string(),
  updates: z.record(
    z.string().regex(/^[a-z][a-z0-9_]*$/, "Invalid column name"),
    z.unknown(),
  ),
});

export const AddCommentInputSchema = z.object({
  transactionId: z.string(),
  text: z.string().min(1),
});

export const AddCommentResponseSchema = z.object({
  success: z.boolean(),
});

export const EditCommentInputSchema = z.object({
  activityId: z.string(),
  text: z.string().min(1),
});

export const EditCommentResponseSchema = z.object({
  success: z.boolean(),
});

export const DeleteCommentInputSchema = z.object({
  activityId: z.string(),
});

export const DeleteCommentResponseSchema = z.object({
  success: z.boolean(),
});

export const UpdateStagedTransactionResponseSchema = z.object({
  success: z.boolean(),
  transaction: TransactionSchema,
});

/**
 * Bulk Update Transactions — apply one field change and/or a comment to many
 * rows at once. `updates` may contain zero or one key; `comment` is optional.
 * At least one of them must be provided.
 */
export const BulkUpdateTransactionsInputSchema = z
  .object({
    transactionIds: z.array(z.string()).min(1),
    updates: z
      .record(
        z.string().regex(/^[a-z][a-z0-9_]*$/, "Invalid column name"),
        z.unknown(),
      )
      .refine((v) => Object.keys(v).length <= 1, {
        message: "At most one field may be updated at a time",
      }),
    comment: z.string().min(1).optional(),
  })
  .refine((v) => Object.keys(v.updates).length > 0 || v.comment, {
    message: "At least one of updates or comment must be provided",
  });

export const BulkUpdateTransactionsResponseSchema = z.object({
  updatedCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  recalculatedCount: z.number().int().nonnegative(),
  commentedCount: z.number().int().nonnegative(),
});

/**
 * Stage Internal Transactions
 */
export const StageInternalTransactionsInputSchema = z.object({
  transactionIds: z.array(z.string()).min(1),
});

export const StageInternalTransactionsResponseSchema = z.object({
  stagedCount: z.number().int().nonnegative(),
});

/**
 * Unstage Internal Transactions
 */
export const UnstageInternalTransactionsInputSchema = z.union([
  z.strictObject({ transactionIds: z.array(z.string()).min(1) }),
  z.strictObject({ all: z.literal(true) }),
]);

export const UnstageInternalTransactionsResponseSchema = z.object({
  unstagedCount: z.number().int().nonnegative(),
});

/**
 * Get Staged Stats
 */
export const GetStagedStatsInputSchema = z.object({});

export const GetStagedStatsResponseSchema = z.object({
  transactionCount: z.number().int().nonnegative(),
  totalDebitAmount: z.number(),
  positiveDebitAmount: z.number(),
  negativeDebitAmount: z.number(),
  vendorCount: z.number().int().nonnegative(),
});

/**
 * Get Staged Supplier Breakdown (for Batch & Send confirmation dialog)
 */
export const GetStagedSupplierBreakdownInputSchema = z.object({
  // When provided, restrict the breakdown to these specific IDs. Used by
  // the Batch and Send dialog so the breakdown matches the actual
  // selection even when filters hide some selected rows.
  transactionIds: z.array(z.string().uuid()).optional(),
  columnFilters: z.record(z.string(), z.array(z.string())).optional(),
  textFilters: z.record(z.string(), z.array(z.string())).optional(),
  dateFilters: z.record(z.string(), z.array(z.string())).optional(),
  numericFilters: z.record(z.string(), z.array(z.string())).optional(),
  hasComments: z.boolean().optional(),
});

export const GetStagedSupplierBreakdownResponseSchema = z.object({
  breakdown: z.array(
    z.object({
      supplierName: z.string(),
      deliveryMethod: z.enum([
        "edi",
        "email",
        "unmatched",
        "missing_duns",
        "missing_email",
      ]),
      count: z.number().int().nonnegative(),
      claimAmount: z.number(),
    }),
  ),
  totalClaimAmount: z.number(),
});

/**
 * Get Selected Claim Amount — sums claim_amount for the given staged
 * transaction IDs. Used by the staged page header to show the running
 * total of the user's current selection.
 */
export const GetSelectedClaimAmountInputSchema = z.object({
  transactionIds: z.array(z.string().uuid()).min(1),
});

export const GetSelectedClaimAmountResponseSchema = z.object({
  claimAmount: z.number(),
});

/**
 * Streaming Excel export (replaces the base64 `download*Excel` procedures).
 *
 * Keyed on the same filter surface as `listIds` rather than an id list, because
 * the download is triggered as a browser navigation to this procedure's GET URL
 * — an id list for a select-all export runs to 100K+ UUIDs and would never fit
 * in a URL, while the filters that produced that selection always do. The
 * response is the workbook's bytes, so nothing is buffered on the server or in
 * the tab and the transfer is a native browser download rather than a `fetch`
 * subject to its ~300s ceiling.
 *
 * `statuses` are derived server-side from `exportKind`, which guarantees the
 * export covers exactly the rows its page lists.
 */
export const SdExportKindSchema = z.enum([
  "staged",
  "pending",
  "staged_internal",
  "no_claim",
]);
export type SdExportKind = z.infer<typeof SdExportKindSchema>;

/**
 * The export request itself. Parked server-side and addressed by a short
 * `selectionId` — see `createExportSelection` — because this payload is
 * unbounded and the download it drives is a browser navigation, whose input has
 * to fit in a URL. Nothing here ever travels in a query string.
 */
export const ExportTransactionsRequestSchema = z
  .object({
    exportKind: SdExportKindSchema,
    columnFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
    textFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
    dateFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
    numericFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
    hasComments: z.boolean().optional(),
    /** Only claims whose DUNS no SPA active on the invoice date names (Schneider). */
    dunsNotOnSpa: z.boolean().optional(),
    search: z.string().optional(),
    searchColumns: z.array(ColumnNameSchema).optional(),
    /**
     * Part of the VIEW definition, like the filters above — the deep-linked
     * subset a page shows for `?transactionIds=...` (see `list`). Without it a
     * select-all inside a deep link resolves against the whole status scope,
     * exporting far more than the page was showing.
     */
    transactionIds: z.array(z.string().uuid()).optional(),
    /**
     * Export exactly these rows, status-scoped but IGNORING the filter fields
     * above — a hand-picked selection is a set of rows, not a view, and the old
     * base64 procedures scoped it the same way (id + status only). The page's
     * selection persists across filter changes, so re-filtering it here would
     * silently drop rows the user had put in their cart.
     */
    includeIds: z.array(z.string().uuid()).optional(),
    /** Drop these rows from the filtered view (select-all minus a few). */
    excludeIds: z.array(z.string().uuid()).optional(),
  })
  // They mean opposite things about the filters (one ignores them, the other
  // narrows them), so accepting both would make the scope ambiguous.
  .refine((input) => !(input.includeIds?.length && input.excludeIds?.length), {
    message: "Pass includeIds or excludeIds, not both",
  });

export const CreateExportSelectionResponseSchema = z.object({
  /** Address of the parked request; the only thing the download URL carries. */
  selectionId: z.string().uuid(),
});

/**
 * The streamed download's whole input.
 *
 * One id, so the URL is a constant ~120 characters whether the user selected 3
 * rows or 90,000 — there is no export size at which this behaves differently.
 * The request it stands for was validated when it was parked and is validated
 * again through {@link ExportTransactionsRequestSchema} when it is redeemed.
 */
export const ExportTransactionsExcelInputSchema = z.object({
  selectionId: z.string().uuid(),
});

/**
 * Get Filter Options
 */
export const GetFilterOptionsInputSchema = z.object({});

export const GetFilterOptionsResponseSchema = z.object({
  division: z.array(z.string()),
  company: z.array(z.string()),
  selling_location: z.array(z.string()),
  trading_partner: z.array(z.string()),
  ship_type: z.array(z.string()),
  rebate_type: z.array(z.string()),
  suppliers: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      /** Whether the "DUNS not on SPA" filter applies to this supplier (Schneider). */
      hasSpaDunsCheck: z.boolean().default(false),
    }),
  ),
  /** Trading partner code → supplier display name (for filter dropdown labels) */
  trading_partner_names: z.record(z.string(), z.string()),
  /** Selling location code → "{code} {Title Case description}" (for branch dropdown labels) */
  selling_location_labels: z.record(z.string(), z.string()),
  /** Company code → "{Name} ({code})" (for company dropdown labels) */
  company_labels: z.record(z.string(), z.string()),
  /** Division code → "{Name} ({code})" (for division dropdown labels) */
  division_labels: z.record(z.string(), z.string()),
});

/**
 * Get User Activity Log (user actions only, no system events)
 */
export const GetActivityLogInputSchema = z.object({
  activityTypes: z.array(z.string()).optional(),
  userIds: z.array(z.string()).optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  limit: z.number().int().positive().max(200).optional().default(50),
  offset: z.number().int().nonnegative().optional().default(0),
});

export const GetActivityLogResponseSchema = z.object({
  activities: z.array(
    z.object({
      id: z.string(),
      activityType: z.string(),
      description: z.string(),
      eventAt: z.string(),
      userId: z.string().nullable(),
      userName: z.string().nullable(),
      transactionId: z.string(),
      invoiceNo: z.string().nullable(),
      division: z.string().nullable(),
      tradingPartner: z.string().nullable(),
      supplierName: z.string().nullable(),
      metadata: z.record(z.string(), z.unknown()).nullable(),
    }),
  ),
  pagination: PaginationSchema,
  filterOptions: z.object({
    activityTypes: z.array(z.string()),
    users: z.array(z.object({ id: z.string(), name: z.string() })),
  }),
  userSummary: z.array(
    z.object({
      userId: z.string(),
      userName: z.string(),
      total: z.number().int().nonnegative(),
      activityCounts: z.record(z.string(), z.number().int().nonnegative()),
    }),
  ),
});

/**
 * Search All Transactions (across all statuses)
 */
export const SearchAllTransactionsInputSchema = z.object({
  divisions: z.array(z.string()).optional(),
  sellingLocations: z.array(z.string()).optional(),
  /** Supplier company UUIDs */
  companyIds: z.array(z.string()).optional(),
  invoiceNo: z.string().optional(),
  orderLineNo: z.string().optional(),
  /** Free-text search against customer name and sold-to/ship-to numbers.
   * Use `*` as a wildcard (e.g. "Acme*", "*Corp", "*Acme*"). With no `*`,
   * matches as a substring. */
  customer: z.string().optional(),
  /** Free-text search across our item #, vendor item #, vendor catalog #, UPC */
  productCode: z.string().optional(),
  /** Free-text substring search against the supplier contract no (SPA quote #) */
  supplierContractNo: z.string().optional(),
  limit: z.number().int().positive().max(200).optional().default(50),
  offset: z.number().int().nonnegative().optional().default(0),
});

export const SearchAllTransactionsResponseSchema = z.object({
  transactions: z.array(
    z.object({
      id: z.string(),
      status: z.string(),
      division: z.string().nullable(),
      sellingLocation: z.string().nullable(),
      invoiceNo: z.string().nullable(),
      orderLineNo: z.string().nullable(),
      tradingPartner: z.string().nullable(),
      supplierName: z.string().nullable(),
      claimAmount: z.number().nullable(),
      supplierContractNo: z.string().nullable(),
      rebateType: z.string().nullable(),
      shipType: z.string().nullable(),
      prefixItemNumber: z.string().nullable(),
      submissionId: z.string().nullable(),
      deliveryMethod: z.string().nullable(),
      // 849 response info for a sent transaction (matched by invoice + order
      // line). Null when the transaction isn't sent or has no matched 849 line.
      responseId: z.string().nullable(),
      responseStatus: z.string().nullable(),
      approvedAmount: z.number().nullable(),
    }),
  ),
  pagination: PaginationSchema,
});

/**
 * Get Classification Stats — per-period counts of AUTO-classified transactions,
 * split by the three classification outcomes (staged / staged internal /
 * no claim).
 */
export const GetClassificationStatsInputSchema = z
  .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
    granularity: z.enum(["day", "week", "month"]).optional().default("day"),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must not be after endDate",
    path: ["endDate"],
  });

export const GetClassificationStatsResponseSchema = z.object({
  daily: z.array(
    z.object({
      date: z.string(),
      staged: z.number().int().nonnegative(),
      staged_internal: z.number().int().nonnegative(),
      no_claim: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
    }),
  ),
  totals: z.object({
    staged: z.number().int().nonnegative(),
    staged_internal: z.number().int().nonnegative(),
    no_claim: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
  }),
});

/**
 * Post Now — manually post all staged transactions (admin action)
 */
export const PostNowInputSchema = z.object({});

export const PostNowResponseSchema = z.object({
  postedCount: z.number().int().nonnegative(),
  /** GL posting workbooks (one per division) */
  postingFiles: z
    .array(
      z.object({
        excel: z.string(),
        filename: z.string(),
        divisionKey: z.string(),
      }),
    )
    .optional(),
  postingWarnings: z
    .array(z.object({ divisionKey: z.string(), message: z.string() }))
    .optional(),
});

/**
 * Download Posting Excel — GL posting workbook for staged/posted transactions
 */
export const DownloadPostingExcelInputSchema = z.object({
  /** Transaction IDs to include. If empty, uses filters to find matching transactions. */
  transactionIds: z.array(z.string()).optional(),
  /** Status filter (defaults to ["staged", "posted"]) */
  statuses: z.array(PostingStatusFilterSchema).optional(),
  /** Exact-match column filters (e.g. division, trading_partner) */
  columnFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  /** Substring (ILIKE) text filters */
  textFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  /** Date range filters */
  dateFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  /** Numeric operator filters (eq:, gte:, lte:) */
  numericFilters: z.record(ColumnNameSchema, z.array(z.string())).optional(),
  /** Filter to only transactions with comments */
  hasComments: z.boolean().optional(),
  /** Free-text search */
  search: z.string().optional(),
  /** Columns to search across */
  searchColumns: z.array(ColumnNameSchema).optional(),
});

export const DownloadPostingExcelResponseSchema = z.object({
  files: z.array(
    z.object({
      excel: z.string(),
      filename: z.string(),
      divisionKey: z.string(),
    }),
  ),
  warnings: z.array(
    z.object({
      divisionKey: z.string(),
      message: z.string(),
    }),
  ),
});

/**
 * Get Status Counts — current transaction counts by status
 */
export const GetStatusCountsInputSchema = z.object({});

export const GetStatusCountsResponseSchema = z.object({
  pending: z.number().int().nonnegative(),
  staged: z.number().int().nonnegative(),
  staged_internal: z.number().int().nonnegative(),
  posted: z.number().int().nonnegative(),
  no_claim: z.number().int().nonnegative(),
});

/**
 * Get Classification Breakdown.
 *
 * Three series that deliberately count different things — do NOT expect them to
 * reconcile with each other:
 *
 *  - `daily`   — classification ACTIVITIES in the window, attributed per event.
 *                A transaction staged, unstaged and re-staged counts twice, and
 *                a transaction the system staged then a user re-classified is
 *                counted on BOTH sides.
 *  - `monthly` — TRANSACTIONS, all-time, bucketed by the month each was first
 *                classified and attributed by the same rule as `totals`.
 *                Summing every month reproduces `totals` exactly.
 *  - `totals`  — TRANSACTIONS, all-time. One per transaction, counted as system
 *                when any of its classification activities was system-driven.
 *
 * `monthly` and `totals` ignore startDate/endDate; only `daily` is windowed.
 */
export const GetClassificationBreakdownInputSchema = z
  .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must not be after endDate",
    path: ["endDate"],
  });

export const GetClassificationBreakdownResponseSchema = z.object({
  daily: z.array(
    z.object({
      date: z.string(),
      system: z.number().int().nonnegative(),
      user: z.number().int().nonnegative(),
    }),
  ),
  /** All-time, one row per month that had a first-time classification. `month` is YYYY-MM-01. */
  monthly: z.array(
    z.object({
      month: z.string(),
      system: z.number().int().nonnegative(),
      user: z.number().int().nonnegative(),
    }),
  ),
  totals: z.object({
    total: z.number().int().nonnegative(),
    classifiedBySystem: z.number().int().nonnegative(),
    classifiedByUser: z.number().int().nonnegative(),
  }),
});

/**
 * Get User Activity Stats — daily counts of all user-triggered actions
 */
export const GetUserActivityStatsInputSchema = z
  .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
    groupByUser: z.boolean().optional().default(false),
  })
  .refine(({ startDate, endDate }) => startDate <= endDate, {
    message: "startDate must not be after endDate",
    path: ["endDate"],
  });

export const GetUserActivityStatsResponseSchema = z.object({
  daily: z.array(
    z.object({
      date: z.string(),
      total: z.number().int().nonnegative(),
    }),
  ),
  dailyByUser: z
    .array(
      z.object({
        date: z.string(),
        userName: z.string(),
        total: z.number().int().nonnegative(),
      }),
    )
    .optional(),
  users: z.array(z.string()).optional(),
});

/**
 * Get Supplier Acceptance Rates — 849 response acceptance breakdown per EDI supplier
 */
export const GetSupplierAcceptanceRatesInputSchema = z
  .object({
    /**
     * Restrict to suppliers on one 844 delivery path. Omit for all.
     * EDI and email reconcile differently, so mixing them hides how each is doing.
     */
    deliveryMethod: z.enum(["edi", "email"]).optional(),
    /**
     * Window on when the claim line was SENT (`sd_844_sent_lines.sent_at`), not
     * when a response arrived — so "sent vs responded" stays a fair comparison
     * over one cohort of claims. Omit either bound for open-ended.
     */
    startDate: z.string().date().optional(),
    endDate: z.string().date().optional(),
  })
  .refine(
    ({ startDate, endDate }) => !startDate || !endDate || startDate <= endDate,
    { message: "startDate must not be after endDate", path: ["endDate"] },
  );

export const GetSupplierAcceptanceRatesResponseSchema = z.object({
  suppliers: z.array(
    z.object({
      supplierId: z.string().nullable(),
      supplierName: z.string(),
      accepted: z.number().int().nonnegative(),
      partial: z.number().int().nonnegative(),
      rejected: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
      sentLineCount: z.number().int().nonnegative(),
    }),
  ),
  totals: z.object({
    accepted: z.number().int().nonnegative(),
    partial: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    sentLineCount: z.number().int().nonnegative(),
  }),
});

/**
 * Contracts
 */
export const e844sdTransactionsContracts = {
  list: oc
    .input(ListSdTransactionsInputSchema)
    .output(ListSdTransactionsResponseSchema)
    .route({
      method: "GET",
      summary: "List ship and debit claim transactions",
      description:
        "Pages through 844 claim transactions by status (pending, staged, internal, no-claim, posted) with column, text, date, numeric, comment and search filters.",
    }),
  listIds: oc
    .input(ListSdTransactionIdsInputSchema)
    .output(ListSdTransactionIdsResponseSchema)
    .route({
      method: "GET",
      summary: "List matching claim transaction ids",
      description:
        "Returns every transaction id matching the given statuses and filters, used for select-all across pages. No pagination.",
    }),
  markAsNoClaim: oc
    .input(MarkAsNoClaimInputSchema)
    .output(MarkAsNoClaimResponseSchema)
    .route({
      method: "POST",
      summary: "Mark claim transactions as no-claim",
      description:
        "Sets the selected transactions to no-claim so they are not submitted to suppliers and logs an activity. Skips sent, processing and posted rows.",
    }),
  unmarkAsNoClaim: oc
    .input(UnmarkAsNoClaimInputSchema)
    .output(UnmarkAsNoClaimResponseSchema)
    .route({
      method: "POST",
      summary: "Return no-claim transactions to pending",
      description:
        "Moves selected no-claim or deleted transactions back to pending and logs an activity per row. Returns how many were changed.",
    }),
  stage: oc
    .input(StageTransactionsInputSchema)
    .output(StageTransactionsResponseSchema)
    .route({
      method: "POST",
      summary: "Stage claim transactions for submission",
      description:
        "Moves selected pending transactions to staged, ready for batch and send to suppliers, and logs an activity. Skips no-claim, already staged and sent rows.",
    }),
  unstage: oc
    .input(UnstageTransactionsInputSchema)
    .output(UnstageTransactionsResponseSchema)
    .route({
      method: "POST",
      summary: "Unstage claim transactions",
      description:
        "Returns selected (or all) staged, unposted transactions to pending and logs an activity. Posted rows are left untouched.",
    }),
  unpost: oc
    .input(UnpostTransactionsInputSchema)
    .output(UnpostTransactionsResponseSchema)
    .route({
      method: "POST",
      summary: "Reverse GL posting on claim transactions",
      description:
        "Clears the posted flag on selected posted transactions so they become editable staged rows again. Requires posting permission; sent rows are never touched.",
    }),
  updateStaged: oc
    .input(UpdateStagedTransactionInputSchema)
    .output(UpdateStagedTransactionResponseSchema)
    .route({
      method: "POST",
      summary: "Edit fields on a claim transaction",
      description:
        "Updates one transaction's fields, recalculating the claim amount when price or quantity change, and logs before/after values. Posted rows allow only a UPC edit.",
    }),
  bulkUpdate: oc
    .input(BulkUpdateTransactionsInputSchema)
    .output(BulkUpdateTransactionsResponseSchema)
    .route({
      method: "POST",
      summary: "Bulk edit claim transactions",
      description:
        "Applies one field change and/or a comment to many pending or resubmission-pending transactions, recalculating claim amounts when pricing changes. Claim amount itself cannot be edited.",
    }),
  addComment: oc
    .input(AddCommentInputSchema)
    .output(AddCommentResponseSchema)
    .route({
      method: "POST",
      summary: "Comment on a claim transaction",
      description: "Adds a user comment to a transaction's activity log.",
    }),
  editComment: oc
    .input(EditCommentInputSchema)
    .output(EditCommentResponseSchema)
    .route({
      method: "POST",
      summary: "Edit a claim transaction comment",
      description:
        "Changes the text of your own transaction comment; allowed only within one hour of posting it.",
    }),
  deleteComment: oc
    .input(DeleteCommentInputSchema)
    .output(DeleteCommentResponseSchema)
    .route({
      method: "POST",
      summary: "Delete a claim transaction comment",
      description:
        "Removes your own transaction comment; allowed only within one hour of posting it.",
    }),
  stageInternal: oc
    .input(StageInternalTransactionsInputSchema)
    .output(StageInternalTransactionsResponseSchema)
    .route({
      method: "POST",
      summary: "Stage claim transactions as internal",
      description:
        "Moves selected transactions to staged-internal (handled internally, not sent to the supplier) and logs an activity. Skips no-claim, staged and sent rows.",
    }),
  unstageInternal: oc
    .input(UnstageInternalTransactionsInputSchema)
    .output(UnstageInternalTransactionsResponseSchema)
    .route({
      method: "POST",
      summary: "Unstage internal claim transactions",
      description:
        "Returns selected (or all) staged-internal transactions to pending and logs an activity.",
    }),
  getStagedStats: oc
    .input(GetStagedStatsInputSchema)
    .output(GetStagedStatsResponseSchema)
    .route({
      method: "GET",
      summary: "Get staged claim totals",
      description:
        "Returns the count, total, positive and negative debit amounts, and supplier count for currently staged transactions.",
    }),
  getStagedSupplierBreakdown: oc
    .input(GetStagedSupplierBreakdownInputSchema)
    .output(GetStagedSupplierBreakdownResponseSchema)
    .route({
      method: "POST",
      summary: "Get staged claims by supplier",
      description:
        "Breaks staged transactions down per supplier with claim totals, optionally narrowed by ids or filters; shown before batching and sending claims.",
    }),
  getSelectedClaimAmount: oc
    .input(GetSelectedClaimAmountInputSchema)
    .output(GetSelectedClaimAmountResponseSchema)
    .route({
      method: "POST",
      summary: "Sum claim amount for selected transactions",
      description:
        "Returns the total claim amount for the given staged transaction ids, shown as the running total of the current selection.",
    }),
  getFilterOptions: oc
    .input(GetFilterOptionsInputSchema)
    .output(GetFilterOptionsResponseSchema)
    .route({
      method: "GET",
      summary: "Get claim transaction filter options",
      description:
        "Returns distinct divisions, companies, branches, trading partners, ship and rebate types, suppliers and display labels for transaction filter dropdowns. May alert the team about unmapped trading partner codes.",
    }),
  /**
   * Park an export request and get back the short id that downloads it.
   *
   * A POST, so the request has a body and no size limit — which is the whole
   * point: `exportExcel` below is a navigation, and a navigation's input has to
   * fit in a URL.
   */
  createExportSelection: oc
    .input(ExportTransactionsRequestSchema)
    .output(CreateExportSelectionResponseSchema)
    .route({
      method: "POST",
      summary: "Park a transactions-export request for a streamed download",
      description:
        "Saves an export request (export kind, filters, included or excluded ids) server-side and returns a short selection id that the Excel download uses.",
    }),
  // The only transaction export. GET so it can be triggered as a browser
  // navigation — see ExportTransactionsExcelInputSchema.
  exportExcel: oc
    .input(ExportTransactionsExcelInputSchema)
    .output(FileStreamSchema)
    .route({
      method: "GET",
      summary:
        "Stream the filtered staged/pending/internal/no-claim transactions as an XLSX download",
      description:
        "Takes a selection id from the export-selection step and streams the matching claim transactions as an Excel workbook.",
    }),
  searchAll: oc
    .input(SearchAllTransactionsInputSchema)
    .output(SearchAllTransactionsResponseSchema)
    .route({
      method: "GET",
      summary: "Search claim transactions across all statuses",
      description:
        "Finds transactions by division, branch, supplier, invoice, order line, customer, product code or contract number, with wildcard customer search. Paginated.",
    }),
  getActivityLog: oc
    .input(GetActivityLogInputSchema)
    .output(GetActivityLogResponseSchema)
    .route({
      method: "GET",
      summary: "Get claim transaction activity log",
      description:
        "Lists user actions on transactions (staging, edits, comments, no-claims) with type, user and date filters, plus per-user summaries. Excludes system events.",
    }),
  getClassificationStats: oc
    .input(GetClassificationStatsInputSchema)
    .output(GetClassificationStatsResponseSchema)
    .route({
      method: "GET",
      summary: "Get auto-classification stats over time",
      description:
        "Returns per-day, week or month counts of automatically classified transactions split into staged, staged-internal and no-claim, plus totals for the date range.",
    }),
  getStatusCounts: oc
    .input(GetStatusCountsInputSchema)
    .output(GetStatusCountsResponseSchema)
    .route({
      method: "GET",
      summary: "Count claim transactions by status",
      description:
        "Returns current counts of pending, staged, internal, no-claim and posted transactions.",
    }),
  getClassificationBreakdown: oc
    .input(GetClassificationBreakdownInputSchema)
    .output(GetClassificationBreakdownResponseSchema)
    .route({
      method: "GET",
      summary: "Get system vs user classification breakdown",
      description:
        "Returns classification activity per day in a date range, plus all-time monthly and total transaction counts split between system and user classification.",
    }),
  getUserActivityStats: oc
    .input(GetUserActivityStatsInputSchema)
    .output(GetUserActivityStatsResponseSchema)
    .route({
      method: "GET",
      summary: "Get daily user activity counts",
      description:
        "Returns daily counts of user-triggered transaction actions in a date range, optionally broken down per user.",
    }),
  getSupplierAcceptanceRates: oc
    .input(GetSupplierAcceptanceRatesInputSchema)
    .output(GetSupplierAcceptanceRatesResponseSchema)
    .route({
      method: "GET",
      summary: "Get supplier claim acceptance rates",
      description:
        "Returns accepted, partial and rejected 849 response counts versus claim lines sent, per supplier and in total, filtered by delivery method and sent date.",
    }),
  postNow: oc.input(PostNowInputSchema).output(PostNowResponseSchema).route({
    method: "POST",
    summary: "Post all staged claims to the GL",
    description:
      "Marks every staged transaction with a configured GL account as posted and returns per-division GL posting workbooks; rows lacking GL setup stay staged with warnings. Requires posting permission.",
  }),
  downloadPostingExcel: oc
    .input(DownloadPostingExcelInputSchema)
    .output(DownloadPostingExcelResponseSchema)
    .route({
      method: "POST",
      summary: "Download GL posting workbook",
      description:
        "Builds per-division GL journal entry workbooks for staged or posted transactions chosen by id or filters, with warnings for rows missing GL setup. Does not post.",
    }),
} as const;

/**
 * Type exports
 */
export type ListSdTransactionsInput = z.infer<
  typeof ListSdTransactionsInputSchema
>;
export type ListSdTransactionsResponse = z.infer<
  typeof ListSdTransactionsResponseSchema
>;
export type ListSdTransactionIdsInput = z.infer<
  typeof ListSdTransactionIdsInputSchema
>;
export type ListSdTransactionIdsResponse = z.infer<
  typeof ListSdTransactionIdsResponseSchema
>;
export type ExportTransactionsExcelInput = z.infer<
  typeof ExportTransactionsExcelInputSchema
>;
export type ExportTransactionsRequest = z.infer<
  typeof ExportTransactionsRequestSchema
>;
export type MarkAsNoClaimInput = z.infer<typeof MarkAsNoClaimInputSchema>;
export type MarkAsNoClaimResponse = z.infer<typeof MarkAsNoClaimResponseSchema>;
export type UnmarkAsNoClaimInput = z.infer<typeof UnmarkAsNoClaimInputSchema>;
export type UnmarkAsNoClaimResponse = z.infer<
  typeof UnmarkAsNoClaimResponseSchema
>;
export type StageTransactionsInput = z.infer<
  typeof StageTransactionsInputSchema
>;
export type StageTransactionsResponse = z.infer<
  typeof StageTransactionsResponseSchema
>;
export type UnstageTransactionsInput = z.infer<
  typeof UnstageTransactionsInputSchema
>;
export type UnstageTransactionsResponse = z.infer<
  typeof UnstageTransactionsResponseSchema
>;
export type UnpostTransactionsInput = z.infer<
  typeof UnpostTransactionsInputSchema
>;
export type UnpostTransactionsResponse = z.infer<
  typeof UnpostTransactionsResponseSchema
>;
export type UpdateStagedTransactionInput = z.infer<
  typeof UpdateStagedTransactionInputSchema
>;
export type UpdateStagedTransactionResponse = z.infer<
  typeof UpdateStagedTransactionResponseSchema
>;
export type BulkUpdateTransactionsInput = z.infer<
  typeof BulkUpdateTransactionsInputSchema
>;
export type BulkUpdateTransactionsResponse = z.infer<
  typeof BulkUpdateTransactionsResponseSchema
>;
export type StageInternalTransactionsInput = z.infer<
  typeof StageInternalTransactionsInputSchema
>;
export type StageInternalTransactionsResponse = z.infer<
  typeof StageInternalTransactionsResponseSchema
>;
export type UnstageInternalTransactionsInput = z.infer<
  typeof UnstageInternalTransactionsInputSchema
>;
export type UnstageInternalTransactionsResponse = z.infer<
  typeof UnstageInternalTransactionsResponseSchema
>;
export type GetFilterOptionsInput = z.infer<typeof GetFilterOptionsInputSchema>;
export type GetFilterOptionsResponse = z.infer<
  typeof GetFilterOptionsResponseSchema
>;
export type GetActivityLogInput = z.infer<typeof GetActivityLogInputSchema>;
export type GetActivityLogResponse = z.infer<
  typeof GetActivityLogResponseSchema
>;
export type SearchAllTransactionsInput = z.infer<
  typeof SearchAllTransactionsInputSchema
>;
export type SearchAllTransactionsResponse = z.infer<
  typeof SearchAllTransactionsResponseSchema
>;
export type GetClassificationStatsInput = z.infer<
  typeof GetClassificationStatsInputSchema
>;
export type GetClassificationStatsResponse = z.infer<
  typeof GetClassificationStatsResponseSchema
>;
export type GetStatusCountsInput = z.infer<typeof GetStatusCountsInputSchema>;
export type GetStatusCountsResponse = z.infer<
  typeof GetStatusCountsResponseSchema
>;
export type GetSupplierAcceptanceRatesInput = z.infer<
  typeof GetSupplierAcceptanceRatesInputSchema
>;
export type GetSupplierAcceptanceRatesResponse = z.infer<
  typeof GetSupplierAcceptanceRatesResponseSchema
>;
export type PostNowInput = z.infer<typeof PostNowInputSchema>;
export type PostNowResponse = z.infer<typeof PostNowResponseSchema>;
export type DownloadPostingExcelInput = z.infer<
  typeof DownloadPostingExcelInputSchema
>;
export type DownloadPostingExcelResponse = z.infer<
  typeof DownloadPostingExcelResponseSchema
>;
